import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, Trash2, ArrowLeft, MessageSquare, Inbox, LogOut, Package, Plus, Pencil, ImagePlus, X, Images, Settings, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { useSite } from "@/site/SiteContext";
import { LANGS } from "@/i18n/translations";
import { uploadFile, mediaSrc } from "@/lib/media";
import { CATALOG_CATS } from "@/data/catalog";
import Logo from "@/components/Logo";

const PORTFOLIO_CATS = ["corniches", "moulures", "colonnes", "interieurs"];
const inp = "w-full bg-background border border-border focus:border-gold rounded-sm px-3 py-2.5 text-sm text-foreground outline-none transition-colors";
const confirmDelete = (msg) => window.confirm(msg);

/* ---------------- Product form ---------------- */
const emptyProduct = { product_id: null, category: "corniches", title: "", description: "", image: "", order: 0, badge: "" };

const ProductForm = ({ initial, onDone, onCancel }) => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image: url }));
    } catch {
      toast.error("Erreur d'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        category: form.category,
        title: form.title,
        description: form.description,
        image: form.image,
        order: Number(form.order) || 0,
        badge: form.badge || "",
      };
      if (form.product_id) await api.put(`/products/${form.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success("Enregistré");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-card border border-gold/20 rounded-sm p-6 mb-6 space-y-4" data-testid="product-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl text-foreground">{form.product_id ? "Modifier le produit" : "Nouveau produit"}</h3>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-gold"><X size={20} /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <select data-testid="product-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inp}>
          {CATALOG_CATS.map((c) => <option key={c.slug} value={c.slug}>{t(`catalog.cats.${c.slug}`)}</option>)}
        </select>
        <select data-testid="product-badge" value={form.badge || ""} onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value }))} className={inp}>
          <option value="">Aucun badge</option>
          <option value="hit">Best-seller (Хит)</option>
          <option value="new">Nouveau (Новинка)</option>
        </select>
        <input data-testid="product-order" type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} placeholder="Ordre" className={inp} />
      </div>
      <input data-testid="product-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Titre du produit" className={inp} />
      <textarea data-testid="product-description" rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" className={`${inp} resize-none`} />
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 border border-dashed border-border hover:border-gold/60 rounded-sm px-4 py-3 cursor-pointer text-muted-foreground hover:text-gold transition-colors text-sm">
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />} Image
          <input type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="product-image" />
        </label>
        {form.image ? <img src={mediaSrc(form.image)} alt="" className="w-16 h-16 object-cover rounded-sm border border-border" /> : null}
      </div>
      <button data-testid="product-save" type="submit" disabled={saving || uploading} className="bg-gold text-black font-sans font-semibold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60">
        {saving ? "…" : "Enregistrer"}
      </button>
    </form>
  );
};

/* ---------------- Portfolio form ---------------- */
const emptyPortfolio = { item_id: null, type: "photo", category: "interieurs", title: "", media_url: "", order: 0 };

const PortfolioForm = ({ initial, onDone, onCancel }) => {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, media_url: url, type: file.type.startsWith("video") ? "video" : "photo" }));
    } catch {
      toast.error("Erreur d'envoi du fichier");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.media_url) { toast.error("Ajoutez un fichier média"); return; }
    setSaving(true);
    try {
      const payload = { type: form.type, category: form.category, title: form.title, media_url: form.media_url, order: Number(form.order) || 0 };
      if (form.item_id) await api.put(`/portfolio/${form.item_id}`, payload);
      else await api.post("/portfolio", payload);
      toast.success("Enregistré");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-card border border-gold/20 rounded-sm p-6 mb-6 space-y-4" data-testid="portfolio-form">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl text-foreground">{form.item_id ? "Modifier le média" : "Nouveau média"}</h3>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-gold"><X size={20} /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <select data-testid="portfolio-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inp}>
          <option value="photo">Photo</option>
          <option value="video">Vidéo</option>
        </select>
        <select data-testid="portfolio-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inp}>
          {PORTFOLIO_CATS.map((c) => <option key={c} value={c}>{t(`portfolio.cats.${c}`)}</option>)}
        </select>
        <input data-testid="portfolio-order" type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} placeholder="Ordre" className={inp} />
      </div>
      <input data-testid="portfolio-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Légende / titre (facultatif)" className={inp} />
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 border border-dashed border-border hover:border-gold/60 rounded-sm px-4 py-3 cursor-pointer text-muted-foreground hover:text-gold transition-colors text-sm">
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />} Fichier (image ou vidéo)
          <input type="file" accept="image/*,video/*" onChange={onMedia} className="hidden" data-testid="portfolio-media" />
        </label>
        {form.media_url ? (
          form.type === "video"
            ? <video src={mediaSrc(form.media_url)} muted className="w-16 h-16 object-cover rounded-sm border border-border" />
            : <img src={mediaSrc(form.media_url)} alt="" className="w-16 h-16 object-cover rounded-sm border border-border" />
        ) : null}
      </div>
      <button data-testid="portfolio-save" type="submit" disabled={saving || uploading} className="bg-gold text-black font-sans font-semibold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60">
        {saving ? "…" : "Enregistrer"}
      </button>
    </form>
  );
};

/* ---------------- Content (site info) form ---------------- */
const SERVICE_IDS = ["corniches", "rosaces", "colonnes", "moulures", "panneaux", "decor", "restauration"];

const ContentForm = () => {
  const { t } = useLang();
  const { reload, contact: siteContact, hero: siteHero, about: siteAbout, services: siteServices } = useSite();
  const [contact, setContact] = useState({ phoneDisplay: "", phoneTel: "", whatsapp: "", email: "" });
  const [hero, setHero] = useState({});
  const [about, setAbout] = useState({});
  const [services, setServices] = useState({});
  const [curLang, setCurLang] = useState("fr");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContact((c) => ({ ...c, ...siteContact }));
    setHero(siteHero || {});
    setAbout(siteAbout || {});
    setServices(siteServices || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setHeroField = (lang, field, v) => setHero((s) => ({ ...s, [lang]: { ...(s[lang] || {}), [field]: v } }));
  const setAboutField = (lang, field, v) => setAbout((s) => ({ ...s, [lang]: { ...(s[lang] || {}), [field]: v } }));
  const setServiceField = (lang, id, field, v) =>
    setServices((s) => ({ ...s, [lang]: { ...(s[lang] || {}), [id]: { ...((s[lang] || {})[id] || {}), [field]: v } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { contact, hero, about, services });
      await reload();
      toast.success("Contenu enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const h = hero[curLang] || {};
  const a = about[curLang] || {};
  const sv = services[curLang] || {};

  return (
    <div className="space-y-8" data-testid="admin-content">
      {/* Contact */}
      <div className="bg-card border border-gold/20 rounded-sm p-6 space-y-4">
        <h3 className="font-serif text-2xl text-foreground">Coordonnées</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Téléphone (affiché)</label>
            <input data-testid="content-phone-display" value={contact.phoneDisplay} onChange={(e) => setContact((c) => ({ ...c, phoneDisplay: e.target.value }))} className={`${inp} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Téléphone (lien tel:)</label>
            <input data-testid="content-phone-tel" value={contact.phoneTel} onChange={(e) => setContact((c) => ({ ...c, phoneTel: e.target.value }))} className={`${inp} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">WhatsApp (chiffres uniquement)</label>
            <input data-testid="content-whatsapp" value={contact.whatsapp} onChange={(e) => setContact((c) => ({ ...c, whatsapp: e.target.value }))} className={`${inp} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">E-mail</label>
            <input data-testid="content-email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} className={`${inp} mt-1.5`} />
          </div>
        </div>
      </div>

      {/* Language selector (shared for Hero / About / Services) */}
      <div className="flex items-center justify-between flex-wrap gap-3 sticky top-0 bg-background/90 backdrop-blur py-3 z-10">
        <span className="text-sm text-muted-foreground font-sans">Langue des textes :</span>
        <div className="flex gap-1.5">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`content-lang-${l.code}`} onClick={() => setCurLang(l.code)}
              className={`px-3 py-1.5 text-xs font-sans uppercase tracking-wider rounded-sm transition-colors ${curLang === l.code ? "bg-gold text-black" : "border border-border text-muted-foreground hover:text-foreground"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="bg-card border border-gold/20 rounded-sm p-6 space-y-3">
        <h3 className="font-serif text-2xl text-foreground">Bannière d'accueil (Hero)</h3>
        <p className="text-xs text-muted-foreground">Laissez vide pour le texte par défaut. Le titre utilise une virgule pour un retour à la ligne doré.</p>
        <input data-testid="content-hero-badge" value={h.badge || ""} onChange={(e) => setHeroField(curLang, "badge", e.target.value)} placeholder="Sous-titre supérieur (badge)" className={inp} />
        <input data-testid="content-hero-title" value={h.title || ""} onChange={(e) => setHeroField(curLang, "title", e.target.value)} placeholder="Titre principal" className={inp} />
        <textarea data-testid="content-hero-subtitle" rows={2} value={h.subtitle || ""} onChange={(e) => setHeroField(curLang, "subtitle", e.target.value)} placeholder="Description" className={`${inp} resize-none`} />
      </div>

      {/* About */}
      <div className="bg-card border border-gold/20 rounded-sm p-6 space-y-3">
        <h3 className="font-serif text-2xl text-foreground">À propos (Atelier)</h3>
        <input data-testid="content-about-title" value={a.title || ""} onChange={(e) => setAboutField(curLang, "title", e.target.value)} placeholder="Titre" className={inp} />
        <input data-testid="content-about-founded" value={a.founded || ""} onChange={(e) => setAboutField(curLang, "founded", e.target.value)} placeholder="Fondé en… (petite ligne dorée)" className={inp} />
        <textarea data-testid="content-about-p1" rows={3} value={a.p1 || ""} onChange={(e) => setAboutField(curLang, "p1", e.target.value)} placeholder="Paragraphe 1" className={`${inp} resize-none`} />
        <textarea data-testid="content-about-p2" rows={3} value={a.p2 || ""} onChange={(e) => setAboutField(curLang, "p2", e.target.value)} placeholder="Paragraphe 2" className={`${inp} resize-none`} />
      </div>

      {/* Services */}
      <div className="bg-card border border-gold/20 rounded-sm p-6 space-y-4">
        <h3 className="font-serif text-2xl text-foreground">Services</h3>
        {SERVICE_IDS.map((id) => (
          <div key={id} className="border-t border-border pt-4 first:border-t-0 first:pt-0">
            <div className="text-[11px] uppercase tracking-widest text-gold mb-2">{t(`services.items.${id}.title`)}</div>
            <input data-testid={`content-service-${id}-title`} value={sv[id]?.title || ""} onChange={(e) => setServiceField(curLang, id, "title", e.target.value)} placeholder="Titre du service" className={`${inp} mb-2`} />
            <textarea data-testid={`content-service-${id}-desc`} rows={2} value={sv[id]?.desc || ""} onChange={(e) => setServiceField(curLang, id, "desc", e.target.value)} placeholder="Description" className={`${inp} resize-none`} />
          </div>
        ))}
      </div>

      <button data-testid="content-save" onClick={save} disabled={saving} className="bg-gold text-black font-sans font-semibold text-sm uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-gold-deep transition-colors disabled:opacity-60">
        {saving ? "…" : "Enregistrer le contenu"}
      </button>
    </div>
  );
};

/* ---------------- Admin page ---------------- */
const AdminPage = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("products");
  const [reviews, setReviews] = useState([]);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingPf, setEditingPf] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rv, ld, pr, pf] = await Promise.all([
        api.get("/reviews"),
        api.get("/admin/leads"),
        api.get("/products"),
        api.get("/portfolio"),
      ]);
      setReviews(rv.data);
      setLeads(ld.data);
      setProducts(pr.data);
      setPortfolio(pf.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (user && typeof user === "object" && user.role === "admin") load();
  }, [user, load]);

  const delReview = async (id) => {
    if (!confirmDelete("Supprimer cet avis ?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((p) => p.filter((r) => r.review_id !== id));
      toast.success("Avis supprimé");
    } catch { toast.error("Erreur"); }
  };

  const delProduct = async (id) => {
    if (!confirmDelete("Supprimer ce produit ?")) return;
    try {
      await api.delete(`/products/${id}`);
      setProducts((p) => p.filter((x) => x.product_id !== id));
      toast.success("Produit supprimé");
    } catch { toast.error("Erreur"); }
  };

  const delPortfolio = async (id) => {
    if (!confirmDelete("Supprimer ce média ?")) return;
    try {
      await api.delete(`/portfolio/${id}`);
      setPortfolio((p) => p.filter((x) => x.item_id !== id));
      toast.success("Média supprimé");
    } catch { toast.error("Erreur"); }
  };

  if (user === null) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">…</div>;
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-5 text-center px-6">
        <Logo size={80} />
        <p className="text-foreground font-serif text-2xl">Accès réservé à l'administrateur</p>
        <Link to="/" className="text-gold hover:underline font-sans text-sm flex items-center gap-2">
          <ArrowLeft size={16} /> Retour au site
        </Link>
      </div>
    );
  }

  const tabBtn = (id, Icon, label, count) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-sans uppercase tracking-wider transition-colors ${
        tab === id ? "bg-gold text-black" : "border border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon size={16} /> {label}{count != null ? ` (${count})` : ""}
    </button>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={40} />
          <span className="font-serif text-xl">
            <span className="gold-gradient-text">A&amp;K</span> Atelier — Admin
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold flex items-center gap-1.5">
            <ArrowLeft size={15} /> Site
          </Link>
          <button onClick={logout} className="text-sm text-muted-foreground hover:text-gold flex items-center gap-1.5">
            <LogOut size={15} /> Sortir
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {tabBtn("products", Package, "Produits", products.length)}
          {tabBtn("portfolio", Images, "Portfolio", portfolio.length)}
          {tabBtn("content", Settings, "Contenu", null)}
          {tabBtn("reviews", MessageSquare, "Avis", reviews.length)}
          {tabBtn("leads", Inbox, "Demandes", leads.length)}
        </div>

        {tab === "products" && (
          <div data-testid="admin-products">
            {editing ? (
              <ProductForm initial={editing} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
            ) : (
              <button data-testid="product-new" onClick={() => setEditing({ ...emptyProduct })} className="mb-6 inline-flex items-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-widest px-5 py-2.5 rounded-sm hover:bg-gold-deep transition-colors">
                <Plus size={16} /> Nouveau produit
              </button>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div key={p.product_id} className="bg-card border border-border rounded-sm overflow-hidden">
                  <div className="h-36 bg-muted">
                    {p.image ? <img src={mediaSrc(p.image)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-widest text-gold">{t(`catalog.cats.${p.category}`)}</div>
                      {p.badge ? (
                        <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${p.badge === "hit" ? "bg-gold text-black" : "bg-emerald-500 text-white"}`}>
                          {p.badge === "hit" ? "Hit" : "New"}
                        </span>
                      ) : null}
                    </div>
                    <div className="font-serif text-lg text-foreground mt-1">{p.title}</div>
                    <div className="mt-3 flex gap-3">
                      <button onClick={() => setEditing({ ...emptyProduct, ...p })} className="text-muted-foreground hover:text-gold transition-colors" data-testid={`product-edit-${p.product_id}`}><Pencil size={16} /></button>
                      <button onClick={() => delProduct(p.product_id)} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`product-del-${p.product_id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div data-testid="admin-portfolio">
            {editingPf ? (
              <PortfolioForm initial={editingPf} onCancel={() => setEditingPf(null)} onDone={() => { setEditingPf(null); load(); }} />
            ) : (
              <button data-testid="portfolio-new" onClick={() => setEditingPf({ ...emptyPortfolio })} className="mb-6 inline-flex items-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-widest px-5 py-2.5 rounded-sm hover:bg-gold-deep transition-colors">
                <Plus size={16} /> Ajouter un média
              </button>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {portfolio.map((p) => (
                <div key={p.item_id} className="bg-card border border-border rounded-sm overflow-hidden">
                  <div className="h-32 bg-muted relative">
                    {p.type === "video"
                      ? <video src={`${mediaSrc(p.media_url)}#t=0.5`} muted preload="metadata" className="w-full h-full object-cover" />
                      : <img src={mediaSrc(p.media_url)} alt="" loading="lazy" className="w-full h-full object-cover" />}
                    {p.type === "video" && <span className="absolute top-2 left-2 text-gold bg-black/60 rounded-sm p-1"><Film size={12} /></span>}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-gold">{t(`portfolio.cats.${p.category}`)}</div>
                    {p.title ? <div className="text-sm text-foreground mt-1 truncate">{p.title}</div> : <div className="text-sm text-muted-foreground/50 mt-1 italic">Sans légende</div>}
                    <div className="mt-2 flex gap-3">
                      <button onClick={() => setEditingPf({ ...emptyPortfolio, ...p })} className="text-muted-foreground hover:text-gold transition-colors" data-testid={`portfolio-edit-${p.item_id}`}><Pencil size={15} /></button>
                      <button onClick={() => delPortfolio(p.item_id)} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`portfolio-del-${p.item_id}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "content" && <ContentForm />}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="admin-reviews">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-card border border-border rounded-sm p-5 flex gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{r.author_name}</span>
                    <span className="flex">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} className="text-gold" fill={n <= r.rating ? "currentColor" : "none"} />)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{r.text}</p>
                  {r.images?.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {r.images.map((src, i) => <img key={i} src={src} alt="" className="w-16 h-16 object-cover rounded-sm border border-border" />)}
                    </div>
                  )}
                </div>
                <button onClick={() => delReview(r.review_id)} className="text-muted-foreground hover:text-red-400 transition-colors h-fit" data-testid={`admin-del-review-${r.review_id}`}><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads">
            {leads.length === 0 && <p className="text-muted-foreground">Aucune demande.</p>}
            {leads.map((l) => (
              <div key={l.id} className="bg-card border border-border rounded-sm p-5">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                  <span className="font-semibold">{l.name}</span>
                  <a href={`tel:${l.phone}`} className="text-gold text-sm">{l.phone}</a>
                  {l.email && <span className="text-sm text-muted-foreground">{l.email}</span>}
                  <span className="text-xs text-muted-foreground uppercase">{l.lang}</span>
                </div>
                {l.message && <p className="mt-2 text-sm text-muted-foreground">{l.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
